import React, {useEffect, useState} from "react";
import {Badge} from "@mui/material";
import Link from "../../../App/components/Link";
import {useDispatch, useSelector} from "react-redux";
import {IRootState} from "../../../App/reducers/store";
import {useDialogue} from "../../hooks/dialogue";
import {IAccount} from "../../../Account/interfaces/account";
import {DialogueActionsTypes, IDialogue} from "../../interfaces/dialogue";
import {DialogueActions} from "../../actions/dialogue";
import isRole from "../../../Auth/hooks/authorize";
import useSound from "use-sound";
const sound = require("../../assets/sound.mp3");

export default function Dialogue(): JSX.Element | null {
  const dispatch: any = useDispatch()
  const {dialogue, dialogues} = useSelector((state: IRootState) => state.dialogue)
  const {account} = useSelector((state: IRootState) => state.account)
  const [isAdmin] = isRole(account, 'admin');
  const [connect, socket] = useDialogue();
  const [operator, setOperator] = useState<{ id: number, user?: IAccount }>()
  const [close, setClose] = useState<{ id: number }>()
  const [create, setCreate] = useState<IDialogue>()
  const [fresh, setFresh] = useState<{ id: number }>()
  const [play, {stop}] = useSound(sound,{volume: 0.75})
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
      dispatch(DialogueActions.dialogues({
        take: 100,
        meta: true
      })).then((response: {
        data: [IDialogue],
        meta?: {
          total?: number
        },
      }) => {
        dispatch({type: DialogueActionsTypes.FETCH_DIALOGUES, payload: {
          data: response.data,
          meta: response.meta ?? dialogues.meta
        }})

        // При полной загрузке/перезагрузке страницы освобождаем диалоги, которые
        // числятся за текущим пользователем (operator_id «прилип» после reload, т.к.
        // cleanup-«leave» drawer'а при hard-refresh не срабатывает). Хаб по 'leave'
        // чистит operator_id в БД и рассылает 'operator'(пусто) — диалог снова свободен.
        if (account) {
          response.data
            .filter(item => !item.deleted && item.operator?.id === account.id)
            .forEach(item => socket.emit("leave", {dialogue: item.id, user: isAdmin ? 'admin' : 'operator'}))
        }

        socket.emit("operators", response.data.map(item => item.id), (operators: Array<{id: number, user?: IAccount}>) => {
          operators.forEach(operator => {
            setOperator(operator)
          })
        })
      })
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const onOperator = (dialogue: number, operator?: IAccount) => {
      setOperator({id: dialogue, user: operator})
    }
    const onFresh = (dialogue: number) => {
      setFresh({id: dialogue})
    }
    const onLeave = (id: number) => {
      setClose({id: id})
    }
    const onCreate = (dialogue: IDialogue) => {
      setCreate(dialogue)
    }

    socket.on('operator', onOperator)
    socket.on('fresh', onFresh)
    socket.on('leave', onLeave)
    socket.on('create', onCreate)

    // Переподписываемся при реконнекте: useDialogue на disconnect делает
    // socket.removeAllListeners(), иначе после обрыва связи уведомления
    // («create»/«fresh»/«operator») перестали бы приходить навсегда.
    return () => {
      socket.off('operator', onOperator)
      socket.off('fresh', onFresh)
      socket.off('leave', onLeave)
      socket.off('create', onCreate)
    }
  }, [connect, socket]);

  useEffect(() => {
    if (operator) {
      dispatch({type: DialogueActionsTypes.FETCH_DIALOGUES, payload: {
          ...dialogues,
          data: dialogues.data.map(dialogue => {
            if (operator.id === dialogue.id) {
              return {
                ...dialogue,
                operator: operator.user,
                viewed: operator.user ? true : dialogue.viewed
              }
            }

            return dialogue
          })
        }})
    }
    // eslint-disable-next-line
  }, [operator])

  useEffect(() => {
    if (close) {
      dispatch({type: DialogueActionsTypes.FETCH_DIALOGUES, payload: {
        ...dialogues,
        data: dialogues.data.filter(dialogue => close.id !== dialogue.id)
      }})

      if (dialogue?.id === close.id) {
        dispatch({type: DialogueActionsTypes.FETCH_DIALOGUE, payload: {
          ...dialogue,
          deleted: new Date()
        }})
      }
    }
    // eslint-disable-next-line
  }, [close])

  useEffect(() => {
    if (create) {
      dispatch({type: DialogueActionsTypes.FETCH_DIALOGUES, payload: {
          ...dialogues,
          data: [
            create,
            ...dialogues.data
          ]
        }})
    }
    // eslint-disable-next-line
  }, [create])

  useEffect(() => {
    if (fresh) {
      const item = dialogues.data.find(dialogue => fresh.id === dialogue.id)

      if (item) {
        dispatch({
          type: DialogueActionsTypes.FETCH_DIALOGUES,
          payload: {
            ...dialogues,
            data: [
              {
                ...item,
                viewed: false
              },
              ...dialogues.data.filter(dialogue => dialogue.id !== item.id)
            ]
          }
        })

        setIsPlaying(true)
      }
    }
    // eslint-disable-next-line
  }, [fresh])

  useEffect(() => {
    if (isPlaying) {
      play()
    }
    setIsPlaying(false)
    // eslint-disable-next-line
  }, [isPlaying])

  useEffect(() => {
    return () => {
      stop()
    }
  }, [stop])

  return (
    <Link to={"/dialogues"}>
      <Badge
        badgeContent={dialogues.data.filter(dialogue => !dialogue.deleted && !dialogue.viewed).length}
        color={dialogues.data.find(dialogue => !dialogue.deleted && !dialogue.viewed) ? "error" : "info"}
      >
        Диалоги
      </Badge>
    </Link>
  )
}